<template>
  <!-- 운영 어시스턴트 패널 — 열려 있을 때만 노출. isPcRedesign(≥1280px)에서 더 넓은 레이아웃 -->
  <section
    ref="panel"
    v-show="store.open"
    class="aw-panel"
    :class="{ 'aw-panel--pc': isPcRedesign }"
    :style="panelStyle"
    role="dialog"
    :aria-label="$t('assistant.title')"
  >
    <!-- 리사이즈 핸들(좌/상/좌상단) — PC(≥1280px) 전용. 태블릿/모바일은 CSS 고정 크기 유지.
      드래그 로직은 usePanelResize, UI는 자식 컴포넌트 -->
    <assistant-resize-handles
      v-if="isPcRedesign"
      :on-start="startResize"
      :on-move="onResizeMove"
      :on-end="endResize"
      :on-reset="resetSize"
    />
    <header class="aw-head" v-on="dragHandlers">
      <div>
        <div class="aw-title">{{ $t('assistant.title') }}</div>
        <div class="aw-sub">{{ $t('assistant.subtitle') }}</div>
      </div>
      <button class="aw-close" type="button" :aria-label="$t('assistant.closeAria')" @click="store.open = false">×</button>
    </header>

    <div ref="msgs" class="aw-messages">
      <template v-for="m in store.messages" :key="m._uid">
        <!-- 구조화 블록(AA-46): BlockRenderer가 type별 분기. draft_confirm은 commit/cancel/refine 위임. -->
        <div v-if="m.kind === 'block'" class="aw-block">
          <block-renderer
            :message="m"
            @commit="commitDraft(m, $event)"
            @cancel="cancelDraft(m)"
            @refine="refineDraft(m, $event)"
          />
          <div v-if="m.restoreNote === 'commitUnknown' || (m.restoreNote === 'expired' && !m.done)" class="aw-restore-note">{{ $t('assistant.restoreNote.' + m.restoreNote) }}</div>
        </div>
        <!-- 일반 채팅 버블(user/bot/error) -->
        <div v-else class="aw-msg" :class="`aw-${m.kind}`">
          <!-- 응답 대기 중 placeholder — 도구 진행 칩(🔧) + 타이핑 인디케이터 -->
          <template v-if="m.kind === 'bot' && m.text === '' && store.sending && !m._started">
            <span v-if="m.statusChip" class="aw-tool-chip">🔧 {{ m.statusChip }}</span>
            <span class="aw-typing"><span></span><span></span><span></span></span>
          </template>
          <!-- 봇 메시지만 마크다운→안전 HTML 렌더(이스케이프 후 변환). 유저/에러는 평문 유지. -->
          <div v-else-if="m.kind === 'bot'" class="aw-md" v-html="renderMarkdown(m.text)"></div>
          <template v-else>{{ displayText(m) }}</template>
        </div>
      </template>
    </div>

    <div class="aw-input-row">
      <textarea
        ref="input"
        v-model="store.draft"
        class="aw-input"
        rows="1"
        :placeholder="$t('assistant.inputPlaceholder')"
        @keydown.enter.exact.prevent="onEnter"
        @input="autoGrow"
      ></textarea>
      <!-- 전송 ↔ 중지 토글(F6): 응답 생성 중에는 ■ 중지 버튼으로 전환 — 클릭 시 스트리밍 abort -->
      <button
        class="aw-send"
        :class="{ 'aw-send--stop': store.sending }"
        type="button"
        :disabled="!store.sending && !store.draft.trim()"
        :aria-label="store.sending ? $t('assistant.stopAria') : $t('assistant.send')"
        :title="store.sending ? $t('assistant.stopAria') : ''"
        @click="store.sending ? cancelSend() : onSend()"
      >
        {{ store.sending ? '■' : $t('assistant.send') }}
      </button>
    </div>
  </section>
</template>

<script>
import { computed, ref, watch, nextTick } from 'vue'
import { useAssistant } from '@/composables/useAssistant'
import { useResponsive } from '@/composables/useResponsive'
import { usePanelResize } from './usePanelResize'
import { usePanelDrag } from './usePanelDrag'
import AssistantResizeHandles from './AssistantResizeHandles.vue'
import BlockRenderer from './BlockRenderer.vue'

// 예: "모델은 'PDH-111'(product_id P1, product_model_id PM1)로 해서 …" → 괄호구만 제거
const ID_CLAUSE_RE = /\s*\((?:product_id|product_model_id|user_group_id)[^()]*\)/g

/**
 * @component AssistantPanel
 * @description 어시스턴트 대화 패널(헤더/메시지/입력). 스트리밍·HITL 로직은
 * useAssistant composable, 상태는 Pinia 스토어. Esc 닫기는 AssistantWidget이 전역 처리.
 * 구조화 블록(AA-46)은 BlockRenderer로 분기 렌더.
 */
export default {
  name: 'AssistantPanel',
  components: { AssistantResizeHandles, BlockRenderer },
  setup() {
    const { store, send, cancelSend, sendBriefing, commitDraft, cancelDraft, renderMarkdown } = useAssistant()
    const { isPcRedesign } = useResponsive()
    // 후보 칩이 만든 사용자 발화의 내부 id 표기는 화면에서만 감춘다 — 전송/이력 원문은 그대로 둔다
    // (LLM 이 그 id 로 도구를 재호출하고 intent 분류도 이 토큰을 본다).
    const displayText = (m) => (m.kind === 'user' ? String(m.text || '').replace(ID_CLAUSE_RE, '') : m.text)
    const { sizeStyle, startResize, onResizeMove, endResize, resetSize } = usePanelResize()
    const panel = ref(null)
    const { positionStyle, dragHandlers, refit } = usePanelDrag(panel)
    // 닫혀 있는 동안 창이 줄었으면 다시 열 때 실제 크기로 위치를 맞춘다(v-show 표시 뒤에 잰다, SURINP-842)
    watch(() => store.open, (opened) => { if (opened) nextTick(refit) })
    // 사용자 지정 크기/위치는 PC(≥1280px)에서만 적용 — 태블릿/모바일은 CSS 브레이크포인트 고정
    const panelStyle = computed(() => (isPcRedesign.value ? { ...sizeStyle.value, ...positionStyle.value } : {}))
    return {
      panel,
      panelStyle,
      dragHandlers,
      store,
      send,
      cancelSend,
      sendBriefing,
      commitDraft,
      cancelDraft,
      renderMarkdown,
      displayText,
      isPcRedesign,
      startResize,
      onResizeMove,
      endResize,
      resetSize
    }
  },
  watch: {
    // 메시지 변동 시 항상 하단으로 스크롤
    'store.messages': {
      handler() { this.scrollDown() },
      deep: true
    },
    // 패널이 열리면 최초 인사 + 프로액티브 브리핑(F1, 세션 내 1회) + 입력 포커스
    'store.open'(opened) {
      if (!opened) return
      if (!this.store.greeted) {
        this.store.pushMessage({ kind: 'bot', text: this.$t('assistant.greeting') })
        this.store.greeted = true
      }
      if (!this.store.briefed) this.sendBriefing()
      this.$nextTick(() => this.$refs.input && this.$refs.input.focus())
    }
  },
  methods: {
    /**
     * Enter 키 핸들러 — 한글 IME 조합 중 Enter 는 무시.
     * v-model 은 compositionend 에야 조합 글자를 반영하므로, 조합 중 전송하면
     * 마지막 글자가 잘린 텍스트가 나간다 (keyCode 229 는 isComposing 미지원 브라우저 보강).
     */
    onEnter(e) {
      if (e.isComposing || e.keyCode === 229) return
      this.onSend()
    },
    /** 전송 핸들러 — composable.send 위임 후 입력창 높이 리셋 */
    async onSend() {
      await this.send()
      this.autoGrow()
    },
    /**
     * 초안 후보 칩 선택 — 기존 초안 카드를 닫고(액션 숨김) 선택 발화를 자동 전송해
     * LLM이 새 초안을 재생성하게 한다 (이전 confirmation_token은 더 쓰지 않음).
     * @param {object} m - draft 카드 메시지
     * @param {string} query - 후보의 자동 전송 발화
     */
    refineDraft(m, query) {
      if (m.committing || m.done || !query) return
      m.done = true
      this.send(query)
    },
    /** textarea 자동 높이 조절(최대 96px) */
    autoGrow() {
      const el = this.$refs.input
      if (!el) return
      el.style.height = 'auto'
      el.style.height = Math.min(el.scrollHeight, 96) + 'px'
    },
    /** 메시지 영역을 최하단으로 스크롤 */
    scrollDown() {
      this.$nextTick(() => {
        const el = this.$refs.msgs
        if (el) el.scrollTop = el.scrollHeight
      })
    }
  }
}
</script>

<style scoped>
.aw-panel {
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 360px;
  /* 100vw는 세로 스크롤바 폭을 포함하므로, 최대 확대 시 좌측 리사이즈 핸들이 화면 밖으로
     밀리지 않도록 스크롤바(최대 17px) 여유를 더해 보수적으로 잡는다 */
  max-width: calc(100vw - 56px);
  height: 520px;
  max-height: calc(100vh - 48px);
  background: var(--color-white, #fff);
  border: 1px solid var(--color-gray-200, #e0e0e0);
  border-radius: var(--radius-lg, 12px);
  box-shadow: var(--shadow-modal, 0 20px 60px rgba(0, 0, 0, 0.2));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 2147483000;
}
/* PC 리디자인(≥1280px): 더 넓은 패널 + 높이 확장 */
.aw-panel--pc {
  width: 420px;
  height: 600px;
}
/* 헤더 드래그(AA-116)는 PC 전용 — 커서/텍스트 선택/스크롤 제스처 간섭 방지 */
@media (min-width: 1280px) {
  .aw-head { cursor: move; user-select: none; touch-action: none; }
}

.aw-head {
  background: var(--color-primary-500, #3B5998);
  color: var(--color-white, #fff);
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.aw-title { font-weight: 700; font-size: 15px; }
.aw-sub { font-size: 11px; opacity: 0.85; margin-top: 2px; }
.aw-close {
  background: none;
  border: none;
  color: inherit;
  font-size: 20px;
  cursor: pointer;
  line-height: 1;
}

.aw-messages {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: var(--color-gray-50, #f5f7fa);
}

.aw-msg {
  max-width: 82%;
  padding: 9px 12px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}
.aw-user {
  align-self: flex-end;
  background: var(--color-primary-500, #3B5998);
  color: var(--color-white, #fff);
  border-bottom-right-radius: 4px;
}
.aw-bot {
  align-self: flex-start;
  background: var(--color-primary-50, #E8EDF4);
  color: var(--color-gray-800, #202124);
  border-bottom-left-radius: 4px;
}
.aw-error {
  align-self: flex-start;
  background: var(--color-danger-light, #F2DCDC);
  color: var(--color-danger, #C03A3A);
  border: 1px solid var(--color-danger, #C03A3A);
}

/* ── 구조화 블록(AA-46) 래퍼: 채팅 버블이 아니라 좌측 정렬 넓은 영역 ── */
.aw-block {
  align-self: flex-start;
  max-width: 100%;
  width: 100%;
}
/* 새로고침 복원 카드 안내(AA-143) — 만료 가능·등록 결과 불명 */
.aw-restore-note { margin-top: 4px; font-size: 12px; color: var(--color-gray-600, #424750); }

/* ── 봇 마크다운 렌더 영역: 가독성 최소 스타일(cs-front 토큰 계열) ──
   v-html로 주입된 노드에는 scoped 속성이 없으므로 반드시 :deep() 필요 (TextBlock.vue와 동일 패턴) */
.aw-md { white-space: normal; }
.aw-md :deep(:first-child) { margin-top: 0; }
.aw-md :deep(:last-child) { margin-bottom: 0; }
.aw-md :deep(h1), .aw-md :deep(h2), .aw-md :deep(h3) {
  margin: 8px 0 4px;
  font-weight: 700;
  line-height: 1.3;
}
.aw-md :deep(h1) { font-size: 16px; }
.aw-md :deep(h2) { font-size: 15px; }
.aw-md :deep(h3) { font-size: 14px; }
.aw-md :deep(p) { margin: 4px 0; }
.aw-md :deep(ul), .aw-md :deep(ol) { margin: 4px 0; padding-left: 20px; }
.aw-md :deep(li) { margin: 2px 0; }
.aw-md :deep(code) {
  background: var(--color-gray-100, #eef0f3);
  border-radius: 4px;
  padding: 1px 4px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12.5px;
}
.aw-md :deep(strong) { font-weight: 700; }
.aw-md :deep(em) { font-style: italic; }
/* 표: 패널 폭 초과 시 가로 스크롤 */
.aw-md :deep(table) {
  display: block;
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  border-collapse: collapse;
  margin: 6px 0;
  font-size: 12.5px;
}
.aw-md :deep(th), .aw-md :deep(td) {
  border: 1px solid var(--color-gray-200, #e0e0e0);
  padding: 4px 8px;
  text-align: left;
  white-space: nowrap;
}
.aw-md :deep(th) {
  background: var(--color-primary-100, #C5D1E3);
  color: var(--color-gray-800, #202124);
  font-weight: 600;
}

/* 도구 진행 칩 — 본문과 분리된 경량 trust-indicator (PRODUCT_UX §2.10 톤) */
.aw-tool-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-right: 8px;
  padding: 2px 8px;
  border-radius: var(--radius-full, 9999px);
  background: var(--color-gray-100, #E8EAED);
  color: var(--color-gray-600, #5f6368);
  font-size: 11.5px;
  font-weight: 600;
}

.aw-typing { display: inline-flex; gap: 4px; }
.aw-typing span {
  width: 6px;
  height: 6px;
  background: var(--color-gray-400, #9aa0a6);
  border-radius: 50%;
  animation: aw-blink 1.2s infinite;
}
.aw-typing span:nth-child(2) { animation-delay: 0.2s; }
.aw-typing span:nth-child(3) { animation-delay: 0.4s; }
@keyframes aw-blink {
  0%, 60%, 100% { opacity: 0.3; }
  30% { opacity: 1; }
}

.aw-input-row {
  display: flex;
  gap: 8px;
  padding: 10px;
  border-top: 1px solid var(--color-gray-200, #e0e0e0);
  background: var(--color-white, #fff);
}
.aw-input {
  flex: 1;
  resize: none;
  border: 1px solid var(--color-gray-200, #e0e0e0);
  border-radius: var(--radius-md, 8px);
  padding: 9px 11px;
  font-size: 14px;
  font-family: inherit;
  max-height: 96px;
  outline: none;
}
.aw-input:focus { border-color: var(--color-primary-500, #3B5998); }
.aw-send {
  background: var(--color-primary-500, #3B5998);
  color: var(--color-white, #fff);
  border: none;
  border-radius: var(--radius-md, 8px);
  padding: 0 16px;
  cursor: pointer;
  font-weight: 600;
}
.aw-send:disabled { background: var(--color-gray-300, #b0bec5); cursor: not-allowed; }
/* 중지 모드(F6) — 중립 톤으로 전환해 전송과 시각 구분 */
.aw-send--stop { background: var(--color-gray-700, #3c4043); }
.aw-send--stop:hover { background: var(--color-gray-800, #202124); }
</style>
